import { useCallback, useMemo } from 'react'

import type { LicenseType } from 'features/licenses/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'

import { prettyLicenseDescription } from 'features/licenses/utils'
import {
  currentRootDomainIdSelector,
  selectDomainDealers,
} from 'features/rootDomains/selectors'
import ReviewDealerTable from './ReviewDealerTable'
import SelectedLicenseTable from '../SelectedLicenseTable'
import {
  ReclaimLicenseModalTitle,
  ReclaimLicenseModalParagraph,
} from './styles'
import { reclaimLicenseThunk } from 'features/licenses/slice'
import type { DomainType } from 'features/domains/types'
import FullscreenModal from 'components/Modal/FullscreenModal'

interface ReclaimLicenseModalProps {
  isOpen: boolean
  handleClose: VoidFunction
  license: LicenseType | null
}

/**
 * @todo localization
 */

function ReclaimLicenseModal({
  isOpen,
  handleClose,
  license,
}: ReclaimLicenseModalProps) {
  const dispatch = useAppDispatch()
  const dealers: DomainType[] | null = useAppSelector(selectDomainDealers.data)
  const currentRootDomainId = useAppSelector(currentRootDomainIdSelector)

  const targetDealer = useMemo(
    () =>
      dealers &&
      license &&
      dealers.find((dealer) => dealer.id === license.domainId),
    [dealers, license]
  )

  const filteredDealers = useMemo(
    () => dealers?.filter((dealer) => dealer.id === license?.domainId) || [],
    [dealers, license?.domainId]
  )

  const handleReclaimLicense = useCallback(async () => {
    if (license?.domainId && license?.id && currentRootDomainId) {
      await dispatch(
        reclaimLicenseThunk(license?.domainId, license?.id, currentRootDomainId)
      )
      handleClose()
    } else {
      console.error(
        `Some data is not defined: `,
        license?.domainId,
        license?.id,
        currentRootDomainId
      )
    }
  }, [
    currentRootDomainId,
    dispatch,
    handleClose,
    license?.domainId,
    license?.id,
  ])

  return (
    <FullscreenModal
      isOpen={isOpen}
      onClose={handleClose}
      headerTitle="Reclaim License"
      footerCloseText="Cancel"
      footerConfirmText="Reclaim"
      onConfirm={handleReclaimLicense}
      disabledConfirm={!targetDealer || !license}
    >
      <>
        <ReclaimLicenseModalTitle>Selected License</ReclaimLicenseModalTitle>
        <SelectedLicenseTable license={license ? [license] : []} />

        <ReclaimLicenseModalParagraph>
          Reclaim this{' '}
          {license && prettyLicenseDescription(license.description)} license
          from {targetDealer ? targetDealer.name : 'your dealer'} and make it
          available to your domain.
        </ReclaimLicenseModalParagraph>

        <ReclaimLicenseModalTitle>Review Dealer</ReclaimLicenseModalTitle>
        <ReviewDealerTable dealers={filteredDealers} />
      </>
    </FullscreenModal>
  )
}

export default ReclaimLicenseModal
